import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Lightbulb, Plus, ThumbsUp, MessageCircle, Flame, Clock, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { timeAgo } from "@/components/Cards";

const STATUS_META = {
  proposed:     { ar: "مقترح",        he: "מוצע",       en: "Proposed",     color: "#6B7280", bg: "#E5E7EB" },
  under_review: { ar: "قيد الدراسة",  he: "בבדיקה",     en: "Under review", color: "#06B6D4", bg: "#CFFAFE" },
  approved:     { ar: "مُعتمَد",      he: "אושר",       en: "Approved",     color: "#673AB7", bg: "#EDE7F6" },
  in_progress:  { ar: "قيد التنفيذ",  he: "בביצוע",     en: "In progress",  color: "#F59E0B", bg: "#FEF3C7" },
  completed:    { ar: "مكتمل",        he: "הושלם",      en: "Completed",    color: "#10B981", bg: "#D1FAE5" },
};

export default function IdeasPage() {
  const { user } = useAuth();
  const { language } = useI18n();
  const [sort, setSort] = useState("top");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ideas?sort=${sort}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [sort]);

  const toggleSupport = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.post(`/ideas/${id}/support`);
      setItems((cur) => cur.map((it) => it.id === id ? { ...it, supported_by_me: data.supported, supporters_count: data.supporters_count } : it));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const SORT_TABS = [
    { id: "top",      label: L("الأكثر دعماً", "המוכי תמיכה", "Most supported"), Icon: Trophy },
    { id: "newest",   label: L("الأحدث",       "החדשים",        "Newest"),         Icon: Clock },
    { id: "trending", label: L("الرائج",       "במגמה",         "Trending"),       Icon: Flame },
  ];

  return (
    <MobileShell
      topBar={
        <>
          <div className="h-14 flex items-center px-4 gap-2">
            <div className="w-9 h-9 rounded-full bg-[#FEF3C7] flex items-center justify-center">
              <Lightbulb size={18} className="text-[#F59E0B]" />
            </div>
            <h1 className="font-extrabold text-[20px] text-[var(--te-text)] flex-1">
              {L("رعيا الافكار للقريه", "רעיונות לקהילה", "Community ideas")}
            </h1>
            {user && (
              <Link
                to="/ideas/new"
                data-testid="idea-create-btn"
                className="te-btn-primary !py-2 !px-3 text-[13px]"
              >
                <Plus size={14} /> {L("فكرة", "רעיון", "Idea")}
              </Link>
            )}
          </div>
          <div className="px-3 py-2 flex items-center gap-1.5 overflow-x-auto te-no-scrollbar border-t border-[var(--te-divider)] bg-white">
            {SORT_TABS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSort(s.id)}
                data-testid={`ideas-sort-${s.id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                  sort === s.id
                    ? "bg-[var(--te-primary)] text-white border-[var(--te-primary)]"
                    : "bg-white text-[var(--te-text)] border-[var(--te-divider)] hover:bg-[var(--te-bg-soft)]"
                }`}
              >
                <s.Icon size={12} /> {s.label}
              </button>
            ))}
          </div>
        </>
      }
    >
      <div className="px-4 pt-4 pb-6">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
        ) : items.length === 0 ? (
          <div className="te-card p-10 text-center te-fade-up" data-testid="ideas-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FEF3C7] flex items-center justify-center mb-3">
              <Lightbulb size={22} className="text-[#F59E0B]" />
            </div>
            <h3 className="font-extrabold text-[16px] text-[var(--te-text)]">
              {L("لا توجد أفكار بعد", "אין רעיונות עדיין", "No ideas yet")}
            </h3>
            <p className="text-[13px] text-[var(--te-text-secondary)] mt-2 max-w-xs mx-auto">
              {L("شارك أول فكرة لتحسين القرية!", "שתף את הרעיון הראשון לשיפור היישוב!", "Share the first idea to improve your community!")}
            </p>
            {user && (
              <Link to="/ideas/new" className="te-btn-primary text-[13px] mt-4 inline-flex" data-testid="ideas-empty-cta">
                <Plus size={14} /> {L("شارك فكرة", "שתף רעיון", "Share an idea")}
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.proposed;
              return (
                <Link
                  key={it.id}
                  to={`/ideas/${it.id}`}
                  data-testid={`idea-card-${it.id}`}
                  className="block te-card te-card-hover overflow-hidden te-fade-up"
                >
                  {it.image_url && (
                    <img src={it.image_url} alt="" className="w-full h-32 object-cover" />
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold"
                        style={{ background: meta.bg, color: meta.color }}
                        data-testid={`idea-status-${it.id}`}
                      >
                        {meta[language] || meta.en}
                      </span>
                      {it.category && (
                        <span className="text-[10px] text-[var(--te-text-muted)] uppercase tracking-wider" dir="auto">{it.category}</span>
                      )}
                      <span className="text-[10px] text-[var(--te-text-muted)] ml-auto">{timeAgo(it.created_at)}</span>
                    </div>
                    <h3 className="font-extrabold text-[16px] text-[var(--te-text)] leading-tight" dir="auto">{it.title}</h3>
                    <p className="text-[12px] text-[var(--te-text-secondary)] mt-1 line-clamp-2 leading-relaxed" dir="auto">{it.description}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--te-divider)]">
                      <span className="text-[11px] text-[var(--te-text-muted)] truncate" dir="auto">
                        {it.author?.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 text-[12px] text-[var(--te-text-muted)]">
                          <MessageCircle size={12} /> {it.comments_count}
                        </span>
                        <button
                          onClick={(e) => toggleSupport(it.id, e)}
                          data-testid={`idea-support-${it.id}`}
                          className={`inline-flex items-center gap-1 text-[12px] font-bold rounded-full px-2.5 py-1 transition-colors ${
                            it.supported_by_me
                              ? "bg-[var(--te-primary-soft)] text-[var(--te-primary)]"
                              : "bg-[var(--te-input-bg)] text-[var(--te-text-secondary)] hover:bg-[#E5E7F3]"
                          }`}
                        >
                          <ThumbsUp size={12} fill={it.supported_by_me ? "currentColor" : "none"} /> {it.supporters_count}
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </MobileShell>
  );
}
